import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, MessageSquare, Calendar, Clock, Building2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { getMiSolicitud } from "@/lib/mybizaclia.functions";
import { SOLICITUD_ESTADO_LABEL } from "@/lib/solicitudes.functions";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/mybizaclia/solicitudes/$id")({
  head: ({ params }) =>
    buildSeo({
      title: "Detalle de solicitud · Bizaclia",
      description: "Detalle de una de tus solicitudes en Bizaclia.",
      path: `/mybizaclia/solicitudes/${params.id}`,
      noindex: true,
    }),
  component: SolicitudDetailPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <main className="container mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold mb-2">No se pudo cargar la solicitud</h1>
        <p className="text-muted-foreground mb-6">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>Reintentar</Button>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="container mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold mb-2">Solicitud no encontrada</h1>
      <Link to="/mybizaclia" search={{ tab: "solicitudes" }} className="text-primary underline">
        Volver a mis solicitudes
      </Link>
    </main>
  ),
});

function SolicitudDetailPage() {
  const { id } = Route.useParams();
  const fn = useServerFn(getMiSolicitud);
  const q = useQuery({
    queryKey: ["mybizaclia", "solicitud", id],
    queryFn: () => fn({ data: { id } }),
  });

  if (q.isLoading) {
    return (
      <main className="container mx-auto max-w-3xl px-4 py-8 space-y-4">
        <div className="h-8 w-1/3 rounded bg-muted animate-pulse" />
        <div className="h-40 rounded-xl bg-muted animate-pulse" />
      </main>
    );
  }
  if (q.error) throw q.error;
  if (!q.data) return null;

  const s = q.data as any;
  const anuncio = s.anuncio;
  const tituloAnuncio = anuncio?.alias ?? anuncio?.referencia ?? "Anuncio";

  return (
    <main className="container mx-auto max-w-3xl px-4 py-8 space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/mybizaclia" search={{ tab: "solicitudes" }}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver a solicitudes
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-xl">Solicitud</CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Enviada el {" "}
                {new Date(s.created_at).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </CardDescription>
            </div>
            <Badge variant="outline" className="w-fit">
              {SOLICITUD_ESTADO_LABEL[s.estado as keyof typeof SOLICITUD_ESTADO_LABEL] ?? s.estado}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {s.solicitud && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MessageSquare className="h-4 w-4" />
                <span>Mensaje</span>
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{s.solicitud}</p>
            </div>
          )}

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ExternalLink className="h-4 w-4" />
              <span>Anuncio relacionado</span>
            </div>
            {anuncio ? (
              <Card className="hover:border-primary/40 transition-colors">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="font-medium">{tituloAnuncio}</p>
                    <p className="text-xs text-muted-foreground">
                      Ref. {anuncio.referencia ?? anuncio.id.slice(0, 8)} · Estado: {" "}
                      {anuncio.estado ?? "—"}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/local/$id" params={{ id: anuncio.id }} search={{} as never}>
                      Ver anuncio <ExternalLink className="h-3.5 w-3.5 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground">No hay anuncio asociado.</p>
            )}
          </div>

          {s.agencia && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span>Agencia</span>
                </div>
                <Card className="hover:border-primary/40 transition-colors">
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="font-medium">{s.agencia.nombre}</p>
                    {s.agencia.slug && (
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/agencia/$id" params={{ id: s.agencia.slug }}>
                          Ver agencia <ExternalLink className="h-3.5 w-3.5 ml-1" />
                        </Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          <Separator />

          <div className="flex flex-col sm:flex-row gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                Creada: {" "}
                {new Date(s.created_at).toLocaleString("es-ES", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            {s.updated_at && s.updated_at !== s.created_at && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  Actualizada: {" "}
                  {new Date(s.updated_at).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
